import { useEffect, useState, useCallback } from 'react';
import { format, startOfDay, endOfDay } from 'date-fns';
import { getCustomerCodes, getDMVShipments, CustomerOption, getBranches, BranchOption } from '../api/shipments';
import { DataTable } from 'components/ui/data-table';
import { dmvColumns } from './columns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortingState {
  id: string;
  desc: boolean;
}

const DMVShipmentsPage = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<SortingState[]>([{ id: 'cn_date', desc: true }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });

  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustId, setSelectedCustId] = useState<string | undefined>(undefined);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [selectedOuCode, setSelectedOuCode] = useState<string | undefined>(undefined);

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { label: 'Shipments', href: '#' },
      { label: 'DMV' }
    ]);
  }, [setBreadcrumbs]);

const fetchCustomerData = useCallback(async () => {
    try {
      const params: any = {};
      
      if (globalFilter) {
        params.search = globalFilter;
      }
      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      params.status = "dmv";

      const response = await getCustomerCodes(params);

      if (response && response.length > 0) {
        const options: CustomerOption[] = response
          .filter(item => item?.cust_code)
          .map(item => ({
            cust_code: item.cust_code,
            cust_name: item.cust_name || item.cust_code,
          }))
          .sort((a, b) => a.cust_name.localeCompare(b.cust_name));

        setCustomerOptions(options);
      } else {
        setCustomerOptions([]);
      }
    } catch (error) {
      console.error("Error fetching customer data for dropdown:", error);
      setCustomerOptions([]);
    }
  }, [globalFilter, dateRange]);

  const fetchBranchData = useCallback(async () => {
    try {
      const branchParams: any = {};
      
      if (globalFilter) {
        branchParams.search = globalFilter;
      }
      if (dateRange.from) {
        branchParams.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        branchParams.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      branchParams.status = "local";

      const branches = await getBranches(branchParams);
      setBranchOptions(branches.sort((a, b) => a.branch_name.localeCompare(b.branch_name)));

    } catch (error) {
      console.error('Error fetching branch data for dropdown:', error);
      setBranchOptions([]);
    }
  }, [globalFilter, dateRange]);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        search: globalFilter,
        sort_by: sorting[0]?.id || 'cn_date',
        sort_order: sorting[0]?.desc ? 'desc' : 'asc',
      };

      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (selectedOuCode) {
        params.booking_branch = selectedOuCode;
      }

      const response = await getDMVShipments(params);
      
      setShipments(response.items || []);
      setTotalCount(response.total_items || 0);
      setPageCount(response.total_pages || 0);
    } catch (error) {
      console.error('Error fetching local shipments:', error);
      setShipments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, sorting, dateRange, selectedCustId, selectedOuCode]);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 300);
    
    setSearchDebounceTimer(timer);
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [globalFilter]);

    useEffect(() => {
      fetchCustomerData();
    }, [fetchCustomerData]);

    useEffect(() => {
      fetchBranchData();
    }, [fetchBranchData]); 

  // Fetch shipments when pagination or sorting changes (including after search reset)
  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const handlePaginationChange = (updater: any) => {
    setPagination(updater);
  };

  const handleSortingChange = (updater: any) => {
    setSorting(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
  };

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange || { from: undefined, to: undefined });
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleCustomerChange = (code: string | undefined) => {
    setSelectedCustId(code);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleBranchChange = (ouCode: string | undefined) => {
    setSelectedOuCode(ouCode);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <DataTable 
      columns={dmvColumns} 
      data={shipments}
      loading={loading}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      globalFilter={globalFilter}
      onGlobalFilterChange={handleGlobalFilterChange}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
      totalCount={totalCount}
      pageCount={pageCount}
      customerOptions={customerOptions}
      selectedCustId={selectedCustId}
      onCustomerChange={handleCustomerChange}
      branchOptions={branchOptions}
      selectedOuCode={selectedOuCode}
      onBranchChange={handleBranchChange}
      alertCards={[]}
      activeAlertFilters={{}}
      onAlertFilterChange={() => {}}
      isLoadingAlerts={false}
    />
  );
};

export default DMVShipmentsPage;

